import angular from 'angular';
import '../../layouts.module';
import template from './command-palette.component.html';
import './command-palette.component.scss';
import { decorateCommand } from '../../../core/services/command-catalog.service';
import { GLOBAL_SEARCH_MIN_LENGTH } from '../../../core/services/global-search.service';

export const COMMAND_SEARCH_DEBOUNCE_MS = 250;

const EDITABLE_TAGS = ['INPUT', 'TEXTAREA', 'SELECT'];

let instanceCount = 0;

function isEditable(target) {
  if (!target || target.nodeType !== 1) return false;
  if (EDITABLE_TAGS.includes(target.tagName)) return true;
  if (target.isContentEditable) return true;
  const editable = target.closest && target.closest('[contenteditable]');
  return Boolean(editable && editable.getAttribute('contenteditable') !== 'false');
}

function isPaletteShortcut(event) {
  return (
    (event.ctrlKey || event.metaKey) &&
    !event.altKey &&
    !event.shiftKey &&
    typeof event.key === 'string' &&
    event.key.toLowerCase() === 'k'
  );
}

class CommandPaletteController {
  constructor(
    CommandCatalogService,
    GlobalSearchService,
    $state,
    $timeout,
    $q,
    $document,
    $element,
    $scope,
    $window
  ) {
    this.CommandCatalogService = CommandCatalogService;
    this.GlobalSearchService = GlobalSearchService;
    this.$state = $state;
    this.$timeout = $timeout;
    this.$q = $q;
    this.$document = $document;
    this.$element = $element;
    this.$scope = $scope;
    this.$window = $window;
  }

  $onInit() {
    instanceCount += 1;
    this.uid = `cp-command-palette-${instanceCount}`;
    this.listboxId = `${this.uid}-listbox`;
    this.term = '';
    this.isOpen = false;
    this.catalog = [];
    this.groups = [];
    this.options = [];
    this.activeIndex = -1;
    this.activeId = undefined;
    this.dataGroups = [];
    this.dataStatus = 'idle';
    this.dataSequence = 0;
    const navigatorInfo = this.$window.navigator || {};
    const platform = `${navigatorInfo.platform || ''} ${navigatorInfo.userAgent || ''}`;
    this.shortcutHint = /Mac|iPhone|iPad/i.test(platform) ? '⌘ K' : 'Ctrl K';
    this.placeholder = this.GlobalSearchService.availableSources().length
      ? 'Pesquisar páginas, atalhos e contatos...'
      : 'Pesquisar páginas e atalhos...';
    this.keydownListener = (event) => this.onGlobalKeydown(event);
    this.$document[0].addEventListener('keydown', this.keydownListener);
  }

  $onDestroy() {
    this.$document[0].removeEventListener('keydown', this.keydownListener);
    this.cancelDataSearch();
  }

  input() {
    return this.$element[0].querySelector('.cp-command-palette__input');
  }

  onGlobalKeydown(event) {
    if (!isPaletteShortcut(event)) return;
    const input = this.input();
    if (!input) return;
    if (event.target !== input && isEditable(event.target)) return;
    event.preventDefault();
    input.focus();
    if (!this.isOpen) this.$scope.$apply(() => this.openPalette());
    input.select();
  }

  openPalette() {
    if (this.isOpen) return;
    this.isOpen = true;
    this.catalog = this.CommandCatalogService.build();
    this.recompute();
  }

  close() {
    if (!this.isOpen) return;
    this.isOpen = false;
    this.setActive(-1);
  }

  onTermChange() {
    this.term = this.term || '';
    if (!this.isOpen) {
      this.isOpen = true;
      this.catalog = this.CommandCatalogService.build();
    }
    this.activeIndex = -1;
    this.scheduleDataSearch();
    this.recompute();
  }

  recompute() {
    const term = this.term.trim();
    let groups;
    if (!term) {
      groups = [
        {
          id: 'recents',
          label: 'Recentes',
          items: this.CommandCatalogService.recents(this.catalog)
        },
        {
          id: 'shortcuts',
          label: 'Ações rápidas',
          items: this.catalog
            .filter((item) => item.group === 'shortcuts')
            .map((item) => decorateCommand(item, ''))
        },
        {
          id: 'pages',
          label: 'Ir para',
          items: this.catalog
            .filter((item) => item.group === 'pages')
            .map((item) => decorateCommand(item, ''))
        }
      ];
    } else {
      groups = [
        {
          id: 'navigation',
          label: 'Navegação',
          items: this.CommandCatalogService.search(this.catalog, term)
        }
      ].concat(this.dataGroups);
    }
    this.groups = groups.filter((group) => group.items.length);
    this.options = [];
    this.groups.forEach((group) => {
      group.items = group.items.map((item) => {
        const option = { ...item, index: this.options.length };
        this.options.push(option);
        return option;
      });
    });
    this.setActive(this.activeIndex < this.options.length ? this.activeIndex : -1);
  }

  optionId(index) {
    return `${this.uid}-option-${index}`;
  }

  groupLabelId(group) {
    return `${this.uid}-group-${group.id}`;
  }

  setActive(index) {
    this.activeIndex = index;
    this.activeId = index >= 0 ? this.optionId(index) : undefined;
  }

  move(step) {
    const total = this.options.length;
    if (!total) return;
    const start = this.activeIndex < 0 ? (step > 0 ? -1 : total) : this.activeIndex;
    this.setActive((start + step + total) % total);
    this.scrollActiveIntoView();
  }

  scrollActiveIntoView() {
    const element = this.$element[0].querySelector(`#${this.activeId}`);
    if (element && typeof element.scrollIntoView === 'function') {
      element.scrollIntoView({ block: 'nearest' });
    }
  }

  onKeydown(event) {
    switch (event.key) {
      case 'ArrowDown':
      case 'ArrowUp':
        event.preventDefault();
        if (!this.isOpen) {
          this.openPalette();
          return;
        }
        this.move(event.key === 'ArrowDown' ? 1 : -1);
        break;
      case 'Enter': {
        if (!this.isOpen) return;
        event.preventDefault();
        const option = this.options[this.activeIndex] || this.options[0];
        if (option) this.select(option);
        break;
      }
      case 'Escape':
        if (!this.isOpen) return;
        event.preventDefault();
        this.close();
        if (this.input()) this.input().focus();
        break;
      case 'Tab':
        this.close();
        break;
      default:
        break;
    }
  }

  select(option) {
    this.CommandCatalogService.remember(option);
    this.close();
    this.term = '';
    this.cancelDataSearch();
    this.dataGroups = [];
    this.dataStatus = 'idle';
    const input = this.input();
    if (input) input.blur();
    const params = option.params || {};
    if (option.params && this.$state.includes(option.state)) {
      return this.$state.go(option.state, params, { reload: option.state });
    }
    return this.$state.go(option.state, params);
  }

  scheduleDataSearch() {
    this.cancelDataSearch();
    this.dataGroups = [];
    const term = this.term.trim();
    if (
      term.length < GLOBAL_SEARCH_MIN_LENGTH ||
      !this.GlobalSearchService.availableSources().length
    ) {
      this.dataStatus = 'idle';
      return;
    }
    this.dataStatus = 'loading';
    this.dataTimer = this.$timeout(() => this.runDataSearch(term), COMMAND_SEARCH_DEBOUNCE_MS);
  }

  runDataSearch(term) {
    this.dataTimer = null;
    this.dataSequence += 1;
    const sequence = this.dataSequence;
    const canceler = this.$q.defer();
    this.dataCanceler = canceler;
    return this.GlobalSearchService.search(term, canceler.promise).then(
      (groups) => {
        if (sequence !== this.dataSequence) return;
        this.dataCanceler = null;
        this.dataGroups = groups
          .filter((group) => group.items.length)
          .map((group) => ({
            id: group.id,
            label: group.label,
            items: group.items.map((item) => decorateCommand(item, term))
          }));
        this.dataStatus = 'done';
        this.recompute();
      },
      () => {
        if (sequence !== this.dataSequence) return;
        this.dataCanceler = null;
        this.dataGroups = [];
        this.dataStatus = 'error';
        this.recompute();
      }
    );
  }

  retryDataSearch() {
    this.scheduleDataSearch();
    if (this.input()) this.input().focus();
  }

  cancelDataSearch() {
    if (this.dataTimer) {
      this.$timeout.cancel(this.dataTimer);
      this.dataTimer = null;
    }
    if (this.dataCanceler) {
      this.dataCanceler.resolve();
      this.dataCanceler = null;
    }
    this.dataSequence += 1;
  }

  get isEmpty() {
    return Boolean(this.term.trim()) && !this.options.length && this.dataStatus !== 'loading';
  }

  get statusMessage() {
    if (!this.isOpen) return '';
    if (this.dataStatus === 'loading') return 'Buscando contatos...';
    if (this.dataStatus === 'error') return 'Não foi possível buscar contatos.';
    if (this.isEmpty) return `Nenhum resultado para "${this.term.trim()}".`;
    const total = this.options.length;
    return total === 1 ? '1 resultado disponível.' : `${total} resultados disponíveis.`;
  }
}

CommandPaletteController.$inject = [
  'CommandCatalogService',
  'GlobalSearchService',
  '$state',
  '$timeout',
  '$q',
  '$document',
  '$element',
  '$scope',
  '$window'
];

angular.module('app.layouts').component('cpCommandPalette', {
  controller: CommandPaletteController,
  controllerAs: 'vm',
  template
});
